import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import {
  Check,
  X,
  Wallet,
  TrendingUp,
  ShoppingCart,
  Coffee,
  Banknote,
  Landmark,
  Tags,
  LineChart,
  PieChart,
  PenLine,
  Home,
  Sparkles,
  Users,
  User,
  ChevronDown,
  ShieldCheck,
  RefreshCw,
  Lock,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// Public App Store URL for the Chequey iOS app (Apple ID 6757507706).
const APP_STORE_URL = 'https://apps.apple.com/app/id6757507706'

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Navbar */}
      <nav className="fixed top-0 z-50 w-full border-b border-gray-200 bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl gradient-primary shadow-md">
              <span className="text-lg font-bold text-white">C</span>
            </div>
            <span className="text-2xl font-extrabold bg-gradient-to-r from-primary-500 to-purple-600 bg-clip-text text-transparent">
              Chequey
            </span>
          </Link>
          <div className="flex items-center gap-6 md:gap-8">
            <a href="#features" className="hidden sm:block text-gray-600 hover:text-primary-500 font-medium transition-colors dark:text-gray-300 dark:hover:text-primary-400">
              Features
            </a>
            <a href="#pricing" className="hidden sm:block text-gray-600 hover:text-primary-500 font-medium transition-colors dark:text-gray-300 dark:hover:text-primary-400">
              Pricing
            </a>
            <a href="#get-the-app" className="hidden sm:block text-gray-600 hover:text-primary-500 font-medium transition-colors dark:text-gray-300 dark:hover:text-primary-400">
              iOS App
            </a>
            <Link to="/login">
              <Button size="sm">Sign In</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
        {/* Decorative blobs (match AuthLayout) */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary-500/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Hero Content */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-medium text-primary-600 dark:border-primary-900/50 dark:bg-primary-900/20 dark:text-primary-300">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                Now on web and iPhone
              </span>
              <h1 className="mt-5 text-5xl lg:text-6xl font-extrabold leading-tight text-gray-900 mb-6 dark:text-gray-100">
                Track Your Expenses.
                <br />
                <span className="bg-gradient-to-r from-primary-500 to-purple-600 bg-clip-text text-transparent">
                  Build Your Wealth.
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed dark:text-gray-300">
                Connect your accounts, categorize transactions, and gain insights into your spending habits with Chequey.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="px-8 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/40 transition-shadow">
                    Get Started Free
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline" className="px-8">
                    Learn More
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <AppStoreBadge href={APP_STORE_URL} />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Take Chequey anywhere — now on iPhone.
                </p>
              </div>
            </div>

            {/* Dashboard Preview */}
            <div className="perspective-1000">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl shadow-primary-500/10 overflow-hidden animate-float dark:border-gray-800 dark:bg-gray-900">
                {/* Window chrome */}
                <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50/80 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50">
                  <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
                  <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
                  <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
                  <span className="ml-2 text-xs font-medium text-gray-400 dark:text-gray-500">Dashboard</span>
                </div>

                <div className="space-y-5 p-5">
                  {/* Net Worth hero stat */}
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Net Worth</span>
                      <Wallet className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                    <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">$841,670</div>
                    <div className="mt-1 flex items-center gap-1 text-sm font-medium text-success">
                      <TrendingUp className="h-4 w-4" />
                      +$12,480 (30d)
                    </div>
                    {/* Net worth trend sparkline */}
                    <div className="mt-3 h-20 w-full">
                      <NetWorthSparkline />
                    </div>
                  </div>

                  {/* Investments + holdings bubbles */}
                  <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4 dark:border-gray-800 dark:bg-gray-800/40">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Investments</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">$185,399</span>
                    </div>
                    <div className="relative mt-2 h-28 overflow-hidden">
                      <BubbleItem ticker="VOO" percent="33.7%" size="large" x={28} y={48} delay={0} />
                      <BubbleItem ticker="NVDA" percent="11.3%" size="medium" x={64} y={32} delay={0.3} />
                      <BubbleItem ticker="QQQ" percent="15.7%" size="medium" x={78} y={66} delay={0.6} />
                      <BubbleItem ticker="VTI" percent="11.6%" size="small" x={46} y={74} delay={0.9} />
                      <BubbleItem ticker="MSFT" percent="6.8%" size="small" x={12} y={74} delay={1.2} />
                      <div
                        className="absolute flex h-9 w-9 items-center justify-center rounded-full border border-primary-200 bg-white/70 text-[10px] font-semibold text-primary-700 shadow-sm animate-bubble-appear dark:border-primary-900/40 dark:bg-gray-900/50 dark:text-primary-200"
                        style={{ left: '88%', top: '22%', transform: 'translate(-50%, -50%)', animationDelay: '1.5s' }}
                      >
                        +4
                      </div>
                    </div>
                  </div>

                  {/* Transactions */}
                  <div className="space-y-3">
                    <TransactionPreview icon={ShoppingCart} tile="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400" name="Whole Foods Market" category="Groceries" amount="-$127.45" />
                    <TransactionPreview icon={Banknote} tile="bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400" name="Salary Deposit" category="Income" amount="+$5,420.00" positive />
                    <TransactionPreview icon={Coffee} tile="bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400" name="Starbucks Coffee" category="Dining" amount="-$6.75" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-gray-100 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            <TrustBadge icon={ShieldCheck} label="Bank-level security" />
            <TrustBadge icon={Landmark} label="Powered by Plaid" />
            <TrustBadge icon={RefreshCw} label="Real-time sync" />
            <TrustBadge icon={Lock} label="Private & encrypted" />
          </div>
        </div>
      </section>

      {/* Features Section — scroll showcase */}
      <section id="features" className="bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 pt-24 pb-4 text-center">
          <Eyebrow>Features</Eyebrow>
          <h2 className="mt-3 text-4xl font-extrabold text-gray-900 dark:text-gray-100">
            Everything You Need to Manage Your Money
          </h2>
          <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
            Scroll to explore what Chequey can do.
          </p>
          <ChevronDown className="mx-auto mt-6 h-6 w-6 animate-bounce text-gray-300 dark:text-gray-600" />
        </div>

        <div className="mx-auto max-w-6xl px-6">
          <ShowcaseRow
            eyebrow="Dashboard"
            icon={LineChart}
            tile="bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400"
            title="Your whole net worth, at a glance"
            description="Cash, investments, and property in one live dashboard. Watch your net worth trend over time with beautiful, real-time charts."
            points={['Assets & liabilities in real time', 'Net worth trend over 7 / 30 / 90 days', 'Spending broken down by category']}
            demo={<DashboardDemo />}
          />
          <ShowcaseRow
            reverse
            eyebrow="Investments"
            icon={TrendingUp}
            tile="bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400"
            title="Track every holding, automatically"
            description="Connect your brokerages and see your full portfolio — stocks and ETFs — with live values, daily change, gains, and cost basis."
            points={['Live prices & daily change', 'Gains and cost basis', 'Per-holding detail']}
            demo={<InvestmentsDemo />}
          />
          <ShowcaseRow
            eyebrow="ETF Breakdown"
            icon={PieChart}
            tile="bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"
            title="See what you really own"
            description="Chequey looks through your ETFs and aggregates the underlying stocks — revealing your true positions across your entire portfolio."
            points={['Look-through holdings', 'Aggregated across all your ETFs', 'Your true position in each stock']}
            demo={<EtfDemo />}
          />
          <ShowcaseRow
            reverse
            eyebrow="Scenario Analysis"
            icon={Sparkles}
            tile="bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-500/15 dark:text-fuchsia-400"
            title="Ask &ldquo;what if?&rdquo; in plain English"
            description="Describe a life event — a market crash, a new job, buying a home — and instantly see how it reshapes your future net worth."
            points={['Natural-language scenarios', 'Baseline vs. projection', 'Stack and save scenarios']}
            demo={<ScenarioDemo />}
          />
          <ShowcaseRow
            eyebrow="Household View"
            icon={Users}
            tile="bg-cyan-50 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-400"
            title="Switch between you and your household"
            description="Toggle from individual to household in one tap to see your shared finances with your partner or family, combined."
            points={['One-tap view switch', 'Combined net worth', 'Per-member breakdown']}
            demo={<HouseholdDemo />}
          />
        </div>

        {/* And much more */}
        <div className="mx-auto max-w-5xl px-6 pt-20 pb-24">
          <p className="mb-8 text-center text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            And much more
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard index={0} icon={Landmark} tile="bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300" title="Bank Connections" description="Securely connect your accounts via Plaid for automatic transaction sync." />
            <FeatureCard index={1} icon={Tags} tile="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400" title="Smart Categorization" description="Auto-categorize transactions and customize categories to match your needs." />
            <FeatureCard index={2} icon={Home} tile="bg-teal-50 text-teal-600 dark:bg-teal-500/15 dark:text-teal-400" title="Real Estate" description="Add your home and properties so your net worth reflects everything you own." />
            <FeatureCard index={3} icon={PenLine} tile="bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400" title="Manual Entries" description="Add cash transactions and notes to keep all your data in one place." />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gray-50 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-3 text-4xl font-extrabold text-gray-900 dark:text-gray-100">
              Simple, Transparent Pricing
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white border border-gray-200 rounded-2xl p-10 shadow-sm hover:shadow-lg transition-shadow dark:border-gray-800 dark:bg-gray-950">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 dark:text-gray-100">Free</h3>
              <div className="mb-8">
                <span className="text-5xl font-extrabold text-gray-900 dark:text-gray-100">$0</span>
                <span className="text-lg text-gray-500 dark:text-gray-400">/forever</span>
              </div>
              <ul className="space-y-4 mb-8">
                <PricingFeature included>Manual transaction entry</PricingFeature>
                <PricingFeature included>Custom categories</PricingFeature>
                <PricingFeature included>Basic net worth tracking</PricingFeature>
                <PricingFeature>Bank connections</PricingFeature>
                <PricingFeature>Investment tracking</PricingFeature>
                <PricingFeature>Automatic sync</PricingFeature>
              </ul>
              <Link to="/register">
                <Button variant="outline" className="w-full" size="lg">Start Free</Button>
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="relative bg-white border-2 border-primary-500 rounded-2xl p-10 shadow-xl shadow-primary-500/10 dark:bg-gray-950">
              <div className="absolute -top-3 right-8 bg-gradient-to-r from-primary-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-md">
                Popular
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 dark:text-gray-100">Pro</h3>
              <div className="mb-2">
                <span className="text-5xl font-extrabold text-gray-900 dark:text-gray-100">$7.99</span>
                <span className="text-lg text-gray-500 dark:text-gray-400">/month</span>
              </div>
              <div className="mb-8">
                <span className="text-3xl font-extrabold text-gray-900 dark:text-gray-100">$79.99</span>
                <span className="text-lg text-gray-500 dark:text-gray-400">/year</span>
                <span className="text-sm font-semibold text-success ml-2">(Save 17%)</span>
              </div>
              <ul className="space-y-4 mb-8">
                <PricingFeature included>Everything in Free</PricingFeature>
                <PricingFeature included>Unlimited bank connections</PricingFeature>
                <PricingFeature included>Automatic transaction sync</PricingFeature>
                <PricingFeature included>Investment tracking</PricingFeature>
                <PricingFeature included>ETF breakdown & aggregation</PricingFeature>
                <PricingFeature included>Advanced insights</PricingFeature>
              </ul>
              <Link to="/register">
                <Button className="w-full" size="lg">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Get the App Section */}
      <section id="get-the-app" className="py-24 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-5xl px-6">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500 to-purple-600 px-8 py-16 text-center shadow-xl shadow-primary-500/20">
            {/* Decorative blobs */}
            <div className="pointer-events-none absolute -top-20 -right-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4">
                Your money, in your pocket
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-xl mx-auto leading-relaxed">
                Track net worth, investments, and spending on the go. The Chequey iOS app stays in sync with everything on the web.
              </p>
              <div className="flex justify-center">
                <AppStoreBadge href={APP_STORE_URL} variant="light" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 dark:border-t dark:border-gray-800 dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl gradient-primary shadow-md">
                  <span className="text-lg font-bold text-white">C</span>
                </div>
                <h3 className="text-2xl font-bold">Chequey</h3>
              </div>
              <p className="text-gray-400">Smart expense tracking for everyone.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <div className="space-y-3">
                <a href="#features" className="block text-gray-400 hover:text-white transition-colors">Features</a>
                <a href="#pricing" className="block text-gray-400 hover:text-white transition-colors">Pricing</a>
                <a href="#get-the-app" className="block text-gray-400 hover:text-white transition-colors">iOS App</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <div className="space-y-3">
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Get the app</h4>
              <AppStoreBadge href={APP_STORE_URL} variant="light" />
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>&copy; 2025 Chequey. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-wider text-primary-500 dark:text-primary-400">
      {children}
    </p>
  )
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 384 512" className={className} fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  )
}

function AppStoreBadge({ href, variant = 'dark' }: { href: string; variant?: 'dark' | 'light' }) {
  const isLight = variant === 'light'
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download on the App Store"
      className={`inline-flex items-center gap-2.5 rounded-xl border px-4 py-2.5 shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
        isLight
          ? 'border-white/30 bg-white text-gray-900 hover:bg-gray-50'
          : 'border-gray-800 bg-gray-900 text-white hover:bg-black dark:border-white/30 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100'
      }`}
    >
      <AppleLogo className="h-7 w-7" />
      <span className="flex flex-col text-left leading-none">
        <span className="text-[10px] font-normal opacity-80">Download on the</span>
        <span className="text-xl font-semibold tracking-tight">App Store</span>
      </span>
    </a>
  )
}

function NetWorthSparkline() {
  return (
    <svg viewBox="0 0 320 80" preserveAspectRatio="none" className="h-full w-full">
      <defs>
        <linearGradient id="nwFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0,62 C36,54 56,58 88,46 C120,34 140,40 172,30 C204,20 224,26 256,16 C284,8 304,12 320,6 L320,80 L0,80 Z"
        fill="url(#nwFill)"
      />
      <path
        d="M0,62 C36,54 56,58 88,46 C120,34 140,40 172,30 C204,20 224,26 256,16 C284,8 304,12 320,6"
        fill="none"
        stroke="#38bdf8"
        strokeWidth="2.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function BubbleItem({ ticker, percent, size, x, y, delay }: { ticker: string; percent: string; size: 'large' | 'medium' | 'small'; x: number; y: number; delay: number }) {
  const sizeClasses = {
    large: 'w-[68px] h-[68px] text-[11px]',
    medium: 'w-[54px] h-[54px] text-[10px]',
    small: 'w-[44px] h-[44px] text-[9px]',
  }
  const percentSizes = {
    large: 'text-[9px]',
    medium: 'text-[8px]',
    small: 'text-[7px]',
  }

  return (
    <div
      className={`absolute ${sizeClasses[size]} flex flex-col items-center justify-center gap-0.5 rounded-full border border-primary-200 bg-primary-50 shadow-sm transition-all hover:scale-110 hover:bg-primary-100 hover:shadow-md cursor-pointer animate-bubble-appear dark:border-primary-900/40 dark:bg-primary-900/30 dark:hover:bg-primary-900/50`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)',
        animationDelay: `${delay}s`,
      }}
    >
      <span className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-200">{ticker}</span>
      <span className={`${percentSizes[size]} font-medium text-primary-700/70 dark:text-primary-200/70`}>{percent}</span>
    </div>
  )
}

function TransactionPreview({ icon: Icon, tile, name, category, amount, positive }: { icon: LucideIcon; tile: string; name: string; category: string; amount: string; positive?: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tile}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{category}</div>
      </div>
      <div className={`shrink-0 text-sm font-semibold ${positive ? 'text-success' : 'text-gray-900 dark:text-gray-100'}`}>
        {amount}
      </div>
    </div>
  )
}

function TrustBadge({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 text-gray-600 dark:text-gray-300">
      <Icon className="h-5 w-5 shrink-0 text-primary-500 dark:text-primary-400" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}

function FeatureCard({ icon: Icon, tile, title, description, index = 0 }: { icon: LucideIcon; tile: string; title: string; description: string; index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.08 }}
      whileHover={{ y: -4 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-[box-shadow,border-color] duration-200 hover:border-primary-200 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-900/50"
    >
      <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${tile}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mb-1.5 text-base font-bold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">{description}</p>
    </motion.div>
  )
}

// ── Scroll showcase: alternating feature rows with animated web-app demos ──

const revealText = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
}
const revealDemo = {
  hidden: { opacity: 0, y: 48, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: 'easeOut' as const } },
}

function ShowcaseRow({
  reverse,
  eyebrow,
  icon: Icon,
  tile,
  title,
  description,
  points,
  demo,
}: {
  reverse?: boolean
  eyebrow: string
  icon: LucideIcon
  tile: string
  title: string
  description: string
  points: string[]
  demo: React.ReactNode
}) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.15 } } }}
      className="grid items-center gap-10 py-14 lg:grid-cols-2 lg:gap-16 lg:py-24"
    >
      <motion.div variants={revealText} className={reverse ? 'lg:order-2' : ''}>
        <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${tile}`}>
          <Icon className="h-6 w-6" />
        </span>
        <p className="mt-5 text-sm font-semibold uppercase tracking-wider text-primary-500 dark:text-primary-400">
          {eyebrow}
        </p>
        <h3 className="mt-2 text-3xl font-extrabold leading-tight text-gray-900 dark:text-gray-100">
          {title}
        </h3>
        <p className="mt-4 text-lg leading-relaxed text-gray-600 dark:text-gray-300">{description}</p>
        <ul className="mt-6 space-y-3">
          {points.map((point) => (
            <li key={point} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
              <Check className="h-5 w-5 shrink-0 text-success" />
              {point}
            </li>
          ))}
        </ul>
      </motion.div>
      <motion.div variants={revealDemo} className={reverse ? 'lg:order-1' : ''}>
        {demo}
      </motion.div>
    </motion.div>
  )
}

function BrowserFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl shadow-primary-500/10 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center gap-1.5 border-b border-gray-100 bg-gray-50/80 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50">
        <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" />
        <span className="ml-2 text-xs font-medium text-gray-400 dark:text-gray-500">{title}</span>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

function MiniStat({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'success' | 'danger' }) {
  const valueColor =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-gray-900 dark:text-gray-100'
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 dark:border-gray-800 dark:bg-gray-800/40">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-base font-bold ${valueColor}`}>{value}</p>
    </div>
  )
}

function DashboardDemo() {
  return (
    <BrowserFrame title="Dashboard">
      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="Net Worth" value="$841,670" />
        <MiniStat label="Assets" value="$1.25M" tone="success" />
        <MiniStat label="Debts" value="$410K" tone="danger" />
      </div>
      <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50/70 p-4 dark:border-gray-800 dark:bg-gray-800/40">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Net Worth Trend
          </span>
          <div className="flex gap-1">
            {['7d', '30d', '90d'].map((d, i) => (
              <span
                key={d}
                className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${
                  i === 1 ? 'bg-primary-500 text-white' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {d}
              </span>
            ))}
          </div>
        </div>
        <div className="mt-3 h-24 w-full">
          <AnimatedSparkline />
        </div>
      </div>
    </BrowserFrame>
  )
}

function AnimatedSparkline() {
  const line =
    'M0,62 C36,54 56,58 88,46 C120,34 140,40 172,30 C204,20 224,26 256,16 C284,8 304,12 320,6'
  return (
    <svg viewBox="0 0 320 80" preserveAspectRatio="none" className="h-full w-full">
      <defs>
        <linearGradient id="dashFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${line} L320,80 L0,80 Z`}
        fill="url(#dashFill)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.5 }}
      />
      <motion.path
        d={line}
        fill="none"
        stroke="#38bdf8"
        strokeWidth="2.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: 'easeInOut' }}
      />
    </svg>
  )
}

function InvestmentsDemo() {
  const rows = [
    { t: 'VOO', n: 'Vanguard S&P 500', v: '$62,400', chg: '+1.2%', up: true },
    { t: 'NVDA', n: 'NVIDIA', v: '$21,000', chg: '+3.4%', up: true },
    { t: 'QQQ', n: 'Invesco QQQ', v: '$29,100', chg: '+0.8%', up: true },
    { t: 'VTI', n: 'Total Market', v: '$18,250', chg: '-0.3%', up: false },
  ]
  return (
    <BrowserFrame title="Investments">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Portfolio Value
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">$185,399</p>
        </div>
        <span className="rounded-lg bg-success/10 px-2.5 py-1 text-sm font-semibold text-success">+$12,340</span>
      </div>
      <div className="mt-4 space-y-2">
        {rows.map((r, i) => (
          <motion.div
            key={r.t}
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
            className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-[10px] font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-200">
                {r.t}
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{r.t}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{r.n}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{r.v}</p>
              <p className={`text-[11px] font-medium ${r.up ? 'text-success' : 'text-danger'}`}>{r.chg}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </BrowserFrame>
  )
}

function EtfDemo() {
  return (
    <BrowserFrame title="ETF Breakdown">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        True holdings across your ETFs
      </p>
      <div className="relative mt-2 h-52 overflow-hidden">
        <BubbleItem ticker="AAPL" percent="18.5%" size="large" x={32} y={42} delay={0} />
        <BubbleItem ticker="MSFT" percent="12.3%" size="medium" x={66} y={28} delay={0.15} />
        <BubbleItem ticker="GOOGL" percent="10.8%" size="medium" x={74} y={64} delay={0.3} />
        <BubbleItem ticker="NVDA" percent="8.2%" size="small" x={44} y={72} delay={0.45} />
        <BubbleItem ticker="TSLA" percent="6.5%" size="small" x={15} y={66} delay={0.6} />
        <BubbleItem ticker="AMZN" percent="5.9%" size="small" x={18} y={22} delay={0.75} />
        <div
          className="absolute flex h-9 w-9 items-center justify-center rounded-full border border-primary-200 bg-white/70 text-[10px] font-semibold text-primary-700 shadow-sm animate-bubble-appear dark:border-primary-900/40 dark:bg-gray-900/50 dark:text-primary-200"
          style={{ left: '90%', top: '46%', transform: 'translate(-50%, -50%)', animationDelay: '0.9s' }}
        >
          +24
        </div>
      </div>
      <p className="mt-1 text-center text-[11px] italic text-gray-400 dark:text-gray-500">
        Aggregated across 3 ETFs
      </p>
    </BrowserFrame>
  )
}

function ScenarioDemo() {
  return (
    <BrowserFrame title="Scenarios">
      <div className="flex items-center gap-2 rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 dark:border-fuchsia-900/40 dark:bg-fuchsia-900/15">
        <Sparkles className="h-4 w-4 shrink-0 text-fuchsia-500" />
        <span className="flex-1 truncate text-xs text-gray-600 dark:text-gray-300">
          What if I buy a $700k home in 2 years?
        </span>
        <span className="rounded-md bg-fuchsia-500 px-2 py-0.5 text-[10px] font-semibold text-white">Analyze</span>
      </div>
      <div className="mt-4 h-32 w-full">
        <ScenarioChart />
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg bg-success/10 px-3 py-2">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Projected impact over 24 months</span>
        <span className="flex items-center gap-1 text-sm font-bold text-success">
          <TrendingUp className="h-4 w-4" />
          +$48,200
        </span>
      </div>
    </BrowserFrame>
  )
}

function ScenarioChart() {
  const baseline = 'M0,104 C60,98 120,94 180,86 C240,78 280,76 320,72'
  const scenario = 'M0,104 C60,92 120,80 180,60 C240,42 280,32 320,22'
  return (
    <svg viewBox="0 0 320 120" preserveAspectRatio="none" className="h-full w-full">
      <defs>
        <linearGradient id="scnFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${scenario} L320,120 L0,120 Z`}
        fill="url(#scnFill)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.8 }}
      />
      <motion.path
        d={baseline}
        fill="none"
        stroke="#94a3b8"
        strokeWidth="2"
        strokeDasharray="5 5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: 'easeInOut' }}
      />
      <motion.path
        d={scenario}
        fill="none"
        stroke="#10b981"
        strokeWidth="2.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.2 }}
      />
    </svg>
  )
}

function HouseholdDemo() {
  const [household, setHousehold] = useState(true)
  useEffect(() => {
    const id = setInterval(() => setHousehold((h) => !h), 2600)
    return () => clearInterval(id)
  }, [])

  return (
    <BrowserFrame title="Household">
      <div className="flex items-start justify-between">
        <div className="relative h-12 flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={household ? 'household' : 'individual'}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="absolute"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {household ? 'Household' : 'Individual'} net worth
              </p>
              <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">
                {household ? '$841,670' : '$420,180'}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Individual / household pill toggle */}
        <div className="flex items-center rounded-full bg-gray-100 p-1 dark:bg-gray-800">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
              !household ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-700 dark:text-primary-400' : 'text-gray-400'
            }`}
          >
            <User className="h-4 w-4" />
          </span>
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
              household ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-700 dark:text-primary-400' : 'text-gray-400'
            }`}
          >
            <Users className="h-4 w-4" />
          </span>
        </div>
      </div>

      {/* Fixed height reserves space for both rows so the toggle loop doesn't shift the page */}
      <div className="mt-4 h-[104px] space-y-2">
        <MemberRow initials="J" name="Jason" value="$420,180" />
        <AnimatePresence>
          {household && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <MemberRow initials="S" name="Sam" value="$421,490" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BrowserFrame>
  )
}

function MemberRow({ initials, name, value }: { initials: string; name: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
          {initials}
        </span>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{name}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  )
}

function PricingFeature({ children, included }: { children: React.ReactNode; included?: boolean }) {
  return (
    <li className="flex items-center gap-3 text-gray-600 py-1 border-b border-gray-100 last:border-0 dark:text-gray-300 dark:border-gray-800">
      {included ? (
        <Check className="w-5 h-5 text-success shrink-0" />
      ) : (
        <X className="w-5 h-5 text-gray-300 shrink-0 dark:text-gray-600" />
      )}
      {children}
    </li>
  )
}
